import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function AProposPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      name: 'À propos — Maître Joël Gautier',
      url: `${siteUrl}/a-propos`,
      mainEntity: {
        '@type': 'Person',
        name: 'Joël Gautier',
        jobTitle: 'Avocat au Barreau de Paris',
        description: 'Plus de 19 ans d\'expérience en droit des affaires et droit pénal. Fondateur du Cabinet Gautier, 91 boulevard Bessières, 75017 Paris.',
        url: `${siteUrl}/a-propos`,
        telephone: '+33183627658',
        email: 'joelgautier.avocat@yahoo.fr',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '91 boulevard Bessières',
          addressLocality: 'Paris',
          postalCode: '75017',
          addressCountry: 'FR',
        },
        alumniOf: [
          { '@type': 'CollegeOrUniversity', name: 'Université de Caen' },
          { '@type': 'CollegeOrUniversity', name: 'Université Paris Sorbonne' },
        ],
        award: [
          'Médaille Défense Nationale — Réserviste volontaire, échelon bronze (2022)',
          'Médaille Jeunesse et Sports — échelon bronze (2025)',
        ],
        knowsAbout: ['Droit des affaires', 'Droit pénal', 'Droit des contrats', 'Droit du sport'],
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-apropos';
    script.textContent = JSON.stringify(schema);
    const existing = document.getElementById('schema-apropos');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-apropos');
      if (el) el.remove();
    };
  }, []);

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b1558608be687494406c07fd3a70dac7.jpeg" alt="Maître Joël Gautier" className="h-28 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">Accueil</Link>
            <Link to="/tarifs" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">Tarifs</Link>
            <Link to="/a-propos" className="text-sm font-medium text-[#C9A961] transition-colors">À propos</Link>
            <Link to="/actualites" className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors">Actualités</Link>
            <button onClick={goToContact} className="text-sm font-medium text-gray-900 hover:text-[#C9A961] transition-colors cursor-pointer bg-transparent border-none">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-widest text-[#C9A961] uppercase mb-4 block">À Propos du Cabinet</span>
              <h1 className="text-6xl font-bold text-[#1A2B4A] mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Maître Joël Gautier
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Plus de 19 ans d'expérience au service de la justice et de la défense des droits. Un engagement sans faille pour chaque client.
              </p>
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#C9A961] mb-2">19+</div>
                  <div className="text-sm text-gray-600">Années d'expérience</div>
                </div>
                <div className="w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#C9A961] mb-2">1500+</div>
                  <div className="text-sm text-gray-600">Dossiers traités</div>
                </div>
                <div className="w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#C9A961] mb-2">98%</div>
                  <div className="text-sm text-gray-600">Clients satisfaits</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b3ded05d71d0716d61d22569f98c463e.jpeg" 
                  alt="Maître Joël Gautier" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#C9A961] text-white p-8 rounded-2xl shadow-xl max-w-xs">
                <i className="ri-double-quotes-l text-4xl mb-4 opacity-50"></i>
                <p className="text-sm leading-relaxed">
                  « La justice n'est pas seulement une profession, c'est une vocation qui exige dévouement et intégrité. »
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Parcours Professionnel</h2>
            <p className="text-xl text-gray-600">Une carrière dédiée à l'excellence juridique</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">

              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-36 text-right">
                  <span className="text-4xl font-bold text-[#C9A961]">2000</span>
                </div>
                <div className="flex-shrink-0 w-px bg-[#C9A961] h-full relative">
                  <div className="w-4 h-4 bg-[#C9A961] rounded-full absolute -left-[7px] top-2"></div>
                </div>
                <div className="flex-1 pb-12">
                  <h3 className="text-3xl font-bold text-[#1A2B4A] mb-3">Parcours universitaire</h3>
                  <ul className="space-y-2 text-xl text-gray-600 leading-relaxed list-none">
                    <li>Études jusqu'au Master 2 à l'Université de Caen.</li>
                    <li>Major de promotion du Master 2 Théorie et Pratique du procès — Université Paris Sorbonne.</li>
                    <li>Major au CRFPA (concours d'entrée à la profession d'avocat).</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-36 text-right">
                  <span className="text-4xl font-bold text-[#C9A961]">2007</span>
                </div>
                <div className="flex-shrink-0 w-px bg-[#C9A961] h-full relative">
                  <div className="w-4 h-4 bg-[#C9A961] rounded-full absolute -left-[7px] top-2"></div>
                </div>
                <div className="flex-1 pb-12">
                  <h3 className="text-3xl font-bold text-[#1A2B4A] mb-3">Création du Cabinet et Activité professionnelle</h3>
                  <ul className="space-y-2 text-xl text-gray-600 leading-relaxed list-none">
                    <li>Première expérience en droit pénal au sein du Cabinet de Maître Jean-Marc Florand.</li>
                    <li>Création du Cabinet Gautier huit mois après la prestation de serment du 27 novembre 2007.</li>
                    <li>Développement d'une clientèle en droit des affaires et droit pénal.</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-36 text-right">
                  <span className="text-4xl font-bold text-[#C9A961]">2016</span>
                </div>
                <div className="flex-shrink-0 w-px bg-[#C9A961] h-full relative">
                  <div className="w-4 h-4 bg-[#C9A961] rounded-full absolute -left-[7px] top-2"></div>
                </div>
                <div className="flex-1 pb-12">
                  <h3 className="text-3xl font-bold text-[#1A2B4A] mb-3">Activités extra-professionnelles</h3>
                  <ul className="space-y-2 text-xl text-gray-600 leading-relaxed list-none">
                    <li>Enseignement dans plusieurs universités.</li>
                    <li>Publication d'articles juridiques dans des revues spécialisées et participation à des conférences nationales.</li>
                    <li>Réserviste.</li>
                    <li>Création d'associations dans les domaines civil, de la défense et sportif, dont le Barreau de Paris Échecs.</li>
                    <li>Élu au comité directeur de la Fédération Française des Échecs en 2021.</li>
                    <li>Élu au Conseil d'administration du Cercle National des Armées en 2022.</li>
                    <li>Élu au conseil municipal de Cormeilles en 2026.</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-36 text-right">
                  <span className="text-4xl font-bold text-[#C9A961]">2018</span>
                </div>
                <div className="flex-shrink-0 w-px bg-[#C9A961] h-full relative">
                  <div className="w-4 h-4 bg-[#C9A961] rounded-full absolute -left-[7px] top-2"></div>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-[#1A2B4A] mb-3">Structuration professionnelle et développement</h3>
                  <ul className="space-y-2 text-xl text-gray-600 leading-relaxed list-none">
                    <li>Installation au 91 boulevard Bessières au sein d'un cabinet individuel.</li>
                    <li>Fonctionnement avec plusieurs avocats dans les domaines du droit des affaires : contentieux commercial, contentieux social, corporate et droit pénal des affaires.</li>
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Nos Valeurs</h2>
            <p className="text-lg text-gray-600">Les principes qui guident notre pratique quotidienne</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Intégrité</h3>
              <p className="text-gray-600 leading-relaxed">
                L'honnêteté et la transparence sont au cœur de notre pratique. Nous nous engageons à toujours agir dans le meilleur intérêt de nos clients avec une éthique irréprochable.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-star-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous visons l'excellence dans chaque dossier, en combinant expertise juridique pointue, rigueur professionnelle et stratégies innovantes pour obtenir les meilleurs résultats.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-heart-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Empathie</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous comprenons que chaque situation juridique est unique et souvent difficile. Notre approche humaine et personnalisée garantit un accompagnement bienveillant et rassurant.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-time-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Disponibilité</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous sommes à votre écoute et disponibles pour répondre à vos questions. Notre réactivité et notre accessibilité sont des atouts majeurs pour une défense efficace.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Innovation</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous restons à la pointe des évolutions juridiques et technologiques pour offrir des solutions modernes et efficaces adaptées aux défis contemporains.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Engagement</h3>
              <p className="text-gray-600 leading-relaxed">
                Nous nous battons avec détermination pour défendre vos droits et vos intérêts. Votre victoire est notre objectif, et nous mettons tout en œuvre pour l'atteindre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Distinctions & Reconnaissances */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Distinctions &amp; Reconnaissances</h2>
            <p className="text-lg text-gray-600">Une expertise reconnue et honorée</p>
          </div>
          <div className="flex flex-col md:flex-row justify-center gap-10 max-w-3xl mx-auto">
            <div className="flex-1 bg-gray-50 rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-[#C9A961]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-medal-line text-[#C9A961] text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">Médaille Défense Nationale</h3>
              <p className="text-gray-600 leading-relaxed">Réserviste volontaire — échelon bronze</p>
              <span className="mt-4 inline-block px-4 py-1 bg-[#C9A961]/10 text-[#C9A961] text-sm font-semibold rounded-full">2022</span>
            </div>
            <div className="flex-1 bg-gray-50 rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-[#C9A961]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-award-line text-[#C9A961] text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">Médaille Jeunesse et Sports</h3>
              <p className="text-gray-600 leading-relaxed">Réserviste volontaire — échelon bronze</p>
              <span className="mt-4 inline-block px-4 py-1 bg-[#C9A961]/10 text-[#C9A961] text-sm font-semibold rounded-full">2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A]">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Prêt à Défendre Vos Droits ?
          </h2>
          <button
            onClick={goToContact}
            className="inline-flex items-center gap-4 px-8 py-4 bg-white text-[#1A2B4A] rounded-full font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            Prendre Rendez-vous
            <span className="w-10 h-10 bg-[#C9A961] rounded-full flex items-center justify-center">
              <i className="ri-calendar-line text-white"></i>
            </span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1B2E] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <i className="ri-scales-line text-[#C9A961] text-[400px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <img src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b1558608be687494406c07fd3a70dac7.jpeg" alt="Maître Joël Gautier" className="h-20 mb-6" />
              <h3 className="text-4xl font-light mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Justice, Intégrité<br />
                & Excellence
              </h3>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h4 className="text-base font-bold mb-4">Nos Services</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit des Affaires</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit Famille</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit Pénal</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Le Cabinet</h4>
                <ul className="space-y-2">
                  <li><Link to="/a-propos" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">À Propos</Link></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Équipe</a></li>
                  <li><Link to="/actualites" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Actualités</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Ressources</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">FAQ</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Guides Juridiques</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Blog</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Contact</h4>
                <ul className="space-y-2">
                  <li><button onClick={goToContact} className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer bg-transparent border-none">Prendre RDV</button></li>
                  <li><button onClick={goToContact} className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer bg-transparent border-none">Nous Écrire</button></li>
                  <li><button onClick={goToContact} className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer bg-transparent border-none">Urgences</button></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-[#C9A961]/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-sm text-white/70">© 2025 Cabinet Joël Gautier. Tous droits réservés.</p>
              <div className="flex gap-5">
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
              </div>
              <div className="flex gap-4 text-sm text-white/70">
                <a href="#" className="hover:text-[#C9A961] transition-colors cursor-pointer">Mentions Légales</a>
                <span>|</span>
                <a href="#" className="hover:text-[#C9A961] transition-colors cursor-pointer">Confidentialité</a>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A961] transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
